import { HttpStatusCode } from 'axios';
import { useMemo } from 'react';
import { useAuthState } from 'state';
import { InvoiceDetail, InvoiceSummary, UploadInvoice } from 'types';
import ApiService from './ApiService';

export class InvoiceService extends ApiService {

    async getInvoiceAdjustmentDetails(accountId: string, vendorId: string, invoiceId: string, paymentIdRef?: string, businessUnit?: string): Promise<InvoiceDetail[]> {
        const { data, status } = await this.get(
            paymentIdRef ? `/api/accounts/${accountId}/vendors/${vendorId}/payments/${paymentIdRef}/adjustments`
                + this.getOptionalQueryParam(false,'invoiceId', invoiceId)
                + this.getOptionalQueryParam(false,'businessUnit', businessUnit)
            : `/api/accounts/${accountId}/vendors/${vendorId}/invoices/${invoiceId}/adjustments`
        );
        return this.IsSuccessStatusCode(status) ? data : [];
    }   

    async getInvoiceDetail(accountId: string, vendorId: string, invoiceId: string): Promise<InvoiceSummary | null> {
        const encodedInvoiceId = encodeURIComponent(invoiceId);
        const { data, status } = await this.get(
            `/api/accounts/${accountId}/vendors/${vendorId}/invoices/${encodedInvoiceId}`
        );
        return this.IsSuccessStatusCode(status) ? data : null;
    }

    async getInvoiceSummaries(accountId: string, voucherId?: string, paymentIdRef?: string, invoiceId?: string, retrieveDays?: string, ): Promise<InvoiceSummary[]> {
        const { data, status } = await this.get(
            `/api/invoice-summaries?accountId=${accountId}`
            + this.getOptionalQueryParam(false,'voucherId', voucherId)
            + this.getOptionalQueryParam(false,'paymentIdRef', paymentIdRef)
            + this.getOptionalQueryParam(false,'invoiceId', invoiceId)
            + this.getOptionalQueryParam(false,'retrieveDays', retrieveDays)
        );
        return this.IsSuccessStatusCode(status) ? data : [];
    }

    async uploadInvoice(data: UploadInvoice, files: File[]) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        files.forEach((file) => formData.append('files', file));
        const { status } = await this.post(`/api/accounts/${data.accountId}/vendors/${data.vendorId}/invoices/documents`, formData, { headers:{ 'Content-Type': 'multipart/form-data' }});
        return status === HttpStatusCode.Ok || status === HttpStatusCode.Created;
    }

    async getPurchaseOrder(accountId: string, poNumber: string) {
        const { data, status } = await this.get(`/api/accounts/${accountId}/purchase-orders/${poNumber}`);
        return this.IsSuccessStatusCode(status) ? data : null;
    }

    async getInvoiceDocuments(accountId: string, vendorId: string, invoiceId: string, voucherPoId: string) {
        const encodedInvoiceId = encodeURIComponent(invoiceId);
        const { data, status } = await this.get(`/api/accounts/${accountId}/vendors/${vendorId}/invoices/${encodedInvoiceId}/documents`
            + this.getOptionalQueryParam(true, 'poNumber', voucherPoId));
        return status === HttpStatusCode.Ok ? data : [];
    }
}

export const useInvoiceService = () => {
    const auth = useAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => new InvoiceService(), [auth]);
};
